import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { X, ChevronDown, ChevronRight, Loader2, Pencil, Trash2, Search } from "lucide-react";
import type { ComandaAberta, ComandaComItens, ComandaItem } from "@caixa/lib/comandasApi";
import { listComandas, getComanda, deleteComanda, expandComandaItens } from "@caixa/lib/comandasApi";
import {
  getAtendimentoLabels,
  parseAtendimentoIdentificacao,
  formatAtendimentoAmbos,
  type ModoAtendimento,
} from "@/lib/atendimentoMode";
import { normalizeSearchText } from "@/lib/searchText";

const POLL_MS = 3000;
/** Altura aproximada de 6 cards colapsados (+ gaps) — scroll só na lista. */
const LIST_MAX_VISIBLE_CLASS = "max-h-[22.5rem]";

/** Itens retornados ao fechar mesa (sem id), para carregar no carrinho */
export type ComandaItemParaCarrinho = {
  id?: number;
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
  subtotal?: number;
  observacao?: string | null;
  cozinha_pendente?: boolean;
  entrada_id?: string;
  desconto?: number;
  promocao_id?: number | null;
  itens_escolhidos?: ComandaItem['itens_escolhidos'];
};

function formatBRL(value: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function normalizeSearch(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase("pt-BR");
}

function comandasListSignature(list: ComandaAberta[]): string {
  return [...list]
    .sort((a, b) => a.id - b.id)
    .map(
      (c) =>
        `${c.id}:${c.total}:${c.updatedAt}:${c.nome}:${c.cliente_nome ?? ""}:${c.cliente_whatsapp ?? ""}:${c.origem ?? ""}`,
    )
    .join("|");
}

function comandaItensSignature(itens: ComandaItem[]): string {
  return itens
    .map((i) => `${i.id}:${i.quantidade}:${i.preco_unitario}:${i.subtotal}:${i.nome_produto}`)
    .join("|");
}

function formatComandaLabel(c: ComandaAberta, modoAtendimento: ModoAtendimento, compact = false): string {
  if (modoAtendimento === "ambos") {
    const parsed = parseAtendimentoIdentificacao(c.nome, "ambos");
    if (compact) return [c.cliente_nome || parsed.nome, c.numero_comanda ?? parsed.numero, c.numero_mesa ?? parsed.mesa]
      .map((value) => value?.trim()).filter(Boolean).join(" ");
    return formatAtendimentoAmbos(c.cliente_nome || parsed.nome, c.numero_comanda ?? parsed.numero, c.numero_mesa ?? parsed.mesa);
  }
  const identificacao = c.nome?.trim() ?? "";
  const parsed = parseAtendimentoIdentificacao(identificacao, modoAtendimento);
  const nomeCliente = c.cliente_nome?.trim() || parsed.nome;

  if (parsed.numero) {
    return nomeCliente ? `${parsed.numero} - ${nomeCliente}` : parsed.numero;
  }

  return nomeCliente || identificacao;
}

type Props = {
  open: boolean;
  modoAtendimento: ModoAtendimento;
  /** Incrementado pelo Caixa após criar/alterar/excluir mesa — dispara refresh imediato. */
  refreshVersion?: number;
  onClose: () => void;
  /** Finalizar mesa: traz itens para o carrinho e fecha só o financeiro */
  onFecharComanda: (
    itens: ComandaItemParaCarrinho[],
    meta?: { jaEnviadoCozinha?: boolean; nome?: string; comandaId?: number },
  ) => void;
  /** Editar mesa: traz itens para o carrinho e entra em modo edição (mantendo a mesa aberta) */
  onEditarComanda: (comandaId: number, nome: string, itens: ComandaItemParaCarrinho[]) => void;
};

export function ComandasListModal({
  open,
  modoAtendimento,
  refreshVersion = 0,
  onClose,
  onFecharComanda,
  onEditarComanda,
}: Props) {
  const labels = getAtendimentoLabels(modoAtendimento);
  const [comandas, setComandas] = useState<ComandaAberta[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedItens, setExpandedItens] = useState<ComandaItem[] | null>(null);
  const [loadingExpand, setLoadingExpand] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [mesaSearch, setMesaSearch] = useState("");

  const pollingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const listSignatureRef = useRef("");
  const expandedIdRef = useRef<number | null>(null);
  const lastRefreshVersionRef = useRef(refreshVersion);

  const comandasFiltradas = useMemo(() => {
    const q = normalizeSearch(mesaSearch);
    if (!q) return comandas;
    if (modoAtendimento !== "ambos") return comandas.filter((c) => normalizeSearch(c.nome).includes(q));
    const numbers = q.match(/\d+/g) ?? [];
    const words = normalizeSearchText(q.replace(/\d+/g, " ").replace(/[·:-]/g, " ")).split(/\s+/).filter(Boolean);
    return comandas.filter((c) => {
      const p = parseAtendimentoIdentificacao(c.nome, "ambos");
      const comanda = c.numero_comanda ?? p.numero;
      const mesa = c.numero_mesa ?? p.mesa ?? "";
      return numbers.every((n) => n === comanda || (mesa !== "" && n.replace(/^0+/, "") === mesa))
        && words.every((word) => normalizeSearchText(formatComandaLabel(c, "ambos")).includes(word));
    });
  }, [comandas, mesaSearch, modoAtendimento]);

  useEffect(() => {
    expandedIdRef.current = expandedId;
  }, [expandedId]);

  const refreshExpandedItens = useCallback(async (list: ComandaAberta[], signal?: AbortSignal) => {
    const id = expandedIdRef.current;
    if (id == null) return;

    const row = list.find((c) => c.id === id);
    if (!row) {
      setExpandedId(null);
      setExpandedItens(null);
      return;
    }

    try {
      const comanda: ComandaComItens = await getComanda(id, { signal });
      const itens = comanda.itens ?? [];
      const nextSig = comandaItensSignature(itens);
      setExpandedItens((prev) => {
        const prevSig = prev ? comandaItensSignature(prev) : "";
        return nextSig === prevSig ? prev : itens;
      });
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      console.error(e);
    }
  }, []);

  const loadList = useCallback(
    async (opts?: { initial?: boolean; immediate?: boolean; signal?: AbortSignal }) => {
      if (pollingRef.current && !opts?.immediate) return;
      pollingRef.current = true;
      if (opts?.initial) setLoading(true);

      try {
        const list = await listComandas({ signal: opts?.signal });
        const sig = comandasListSignature(list);
        if (sig !== listSignatureRef.current) {
          listSignatureRef.current = sig;
          setComandas(list);
          await refreshExpandedItens(list, opts?.signal);
        } else if (opts?.immediate) {
          await refreshExpandedItens(list, opts?.signal);
        }
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        console.error(e);
      } finally {
        pollingRef.current = false;
        if (opts?.initial) setLoading(false);
      }
    },
    [refreshExpandedItens],
  );

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      listSignatureRef.current = "";
      lastRefreshVersionRef.current = refreshVersion;
      setMesaSearch("");
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    lastRefreshVersionRef.current = refreshVersion;
    setExpandedId(null);
    setExpandedItens(null);
    setMesaSearch("");

    const poll = (initial: boolean) => {
      if (!initial && document.hidden) return;
      void loadList({ initial, signal: controller.signal });
    };

    poll(true);
    const intervalId = window.setInterval(() => poll(false), POLL_MS);

    const onVisibility = () => {
      if (!document.hidden) poll(false);
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [open, loadList]);

  useEffect(() => {
    if (!open) return;
    if (refreshVersion === lastRefreshVersionRef.current) return;
    lastRefreshVersionRef.current = refreshVersion;
    const signal = abortRef.current?.signal;
    if (!signal || signal.aborted) return;
    void loadList({ immediate: true, signal });
  }, [refreshVersion, open, loadList]);

  const handleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedItens(null);
      return;
    }
    setLoadingExpand(true);
    try {
      const comanda: ComandaComItens = await getComanda(id);
      setExpandedId(id);
      setExpandedItens(comanda.itens ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingExpand(false);
    }
  };

  const handleFechar = async (id: number) => {
    setActionId(id);
    try {
      const comanda = await getComanda(id);
      const itens = expandComandaItens(comanda.itens ?? []);
      const row = comandas.find((c) => c.id === id);
      onFecharComanda(itens, {
        jaEnviadoCozinha: itens.some((item) => item.cozinha_pendente === true),
        nome: comanda.nome || row?.nome,
        comandaId: comanda.id ?? id,
      });
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setActionId(null);
    }
  };

  const handleEditar = async (c: ComandaAberta) => {
    setActionId(c.id);
    try {
      const comanda = await getComanda(c.id);
      const itens: ComandaItemParaCarrinho[] = expandComandaItens(comanda.itens ?? []).map((i) => ({
        id: i.id,
        produto_id: i.produto_id,
        nome_produto: i.nome_produto,
        quantidade: i.quantidade,
        preco_unitario: i.preco_unitario,
        subtotal: i.subtotal,
        observacao: i.observacao ?? null,
        cozinha_pendente: i.cozinha_pendente === true,
        entrada_id: i.entrada_id,
        desconto: i.desconto,
        promocao_id: i.promocao_id,
        itens_escolhidos: i.itens_escolhidos,
      }));
      onEditarComanda(c.id, c.nome, itens);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setActionId(null);
    }
  };

  const handleExcluir = async (id: number) => {
    setActionId(id);
    try {
      await deleteComanda(id);
      listSignatureRef.current = "";
      setComandas((prev) => prev.filter((c) => c.id !== id));
      if (expandedId === id) {
        setExpandedId(null);
        setExpandedItens(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setActionId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="caixa-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="comandas-list-title"
        className="caixa-modal caixa-modal--lg caixa-modal--stack"
        style={{ width: "min(33rem, 100%)", maxWidth: "33rem" }}
      >
        <div className="caixa-modal-section--head" style={{ display: "block" }}>
          <div className="flex items-center justify-between gap-3">
            <h2 id="comandas-list-title" className="caixa-modal-title">
              {labels.abertas}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="caixa-modal-close"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="relative mt-3">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a3a3a3]"
              aria-hidden
            />
            <input
              type="text"
              autoComplete="off"
              value={mesaSearch}
              onChange={(e) => setMesaSearch(e.target.value)}
              placeholder={`Pesquisar ${labels.singularLower}...`}
              aria-label={`Pesquisar ${labels.singularLower}`}
              className="caixa-input caixa-input--333 pl-9 pr-9"
            />
            {mesaSearch ? (
              <button
                type="button"
                onClick={() => setMesaSearch("")}
                className="caixa-modal-close absolute right-2 top-1/2 -translate-y-1/2"
                aria-label="Limpar pesquisa"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
        <div className={`caixa-modal-section--body ${LIST_MAX_VISIBLE_CLASS}`}>
          {loading && comandas.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : comandas.length === 0 ? (
            <p className="text-zinc-400 text-center py-8">Nenhuma {labels.singularLower} aberta.</p>
          ) : comandasFiltradas.length === 0 ? (
            <p className="text-zinc-400 text-center py-8">Nenhuma {labels.singularLower} encontrada.</p>
          ) : (
            <ul className="space-y-2">
              {comandasFiltradas.map((c) => (
                <li
                  key={c.id}
                  className="rounded-lg border border-[#3a3a3a] bg-[#242424] overflow-hidden"
                >
                  <div className={modoAtendimento === "ambos" ? "overflow-x-auto" : undefined}>
                  <div className={`flex items-center gap-2 p-[.6rem] ${modoAtendimento === "ambos" ? "flex-nowrap min-w-[27rem]" : "flex-wrap"}`}>
                    <button
                      type="button"
                      onClick={() => handleExpand(c.id)}
                      className="flex flex-1 items-center gap-2 min-w-0 text-left cursor-pointer hover:bg-[#333333] rounded p-1 -m-1"
                    >
                      {expandedId === c.id ? (
                        <ChevronDown className="w-5 h-5 text-zinc-400 shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-zinc-400 shrink-0" />
                      )}
                      <span className="text-[14px] font-medium text-white min-w-0 flex-1 truncate">{formatComandaLabel(c, modoAtendimento, true)}</span>
                      <span className="text-[14px] text-emerald-400 font-semibold shrink-0 tabular-nums">{formatBRL(c.total)}</span>
                    </button>
                    <div className={`flex gap-2 shrink-0 items-center ${modoAtendimento === "ambos" ? "ml-auto" : ""}`} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleFechar(c.id)}
                        disabled={actionId !== null}
                        className="caixa-btn-success caixa-btn-compact"
                      >
                        {actionId === c.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Finalizando…
                          </>
                        ) : (
                          "Finalizar"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditar(c)}
                        disabled={actionId !== null}
                        className="caixa-btn-ghost caixa-btn-compact p-1.5"
                        aria-label={`Editar ${labels.singularLower}`}
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleExcluir(c.id)}
                        disabled={actionId !== null}
                        className="p-1.5 rounded-md bg-[#333333] text-zinc-200 hover:bg-red-600 hover:text-white disabled:opacity-50"
                        aria-label={`Excluir ${labels.singularLower}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  </div>
                  {expandedId === c.id && (
                    <div className="border-t border-[#3a3a3a] bg-[#242424] p-3">
                      {loadingExpand ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                        </div>
                      ) : expandedItens && expandedItens.length > 0 ? (
                        <ul className="space-y-2 text-sm">
                          {expandedItens.map((i) => (
                            <li
                              key={i.id}
                              className="text-zinc-300"
                            >
                              <div className="flex justify-between gap-2">
                                <span>
                                  {i.nome_produto} — {i.quantidade} x {formatBRL(i.preco_unitario)}
                                </span>
                                <span>{formatBRL(i.subtotal)}</span>
                              </div>
                              {i.observacao?.trim() ? (
                                <p className="mt-0.5 text-xs text-zinc-400 whitespace-pre-wrap break-words">
                                  {i.observacao.trim()}
                                </p>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-zinc-500 text-sm">Nenhum item.</p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
